import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { MultiSelect } from "primereact/multiselect";
import { InputTextarea } from "primereact/inputtextarea";

import Layout from "../../component/layout";
import { makeRequest2 } from "../../../../utils/makeRequest";
import { toastify } from "../../../../utils/toast";
import { useFacilityStore } from "../../../../app/z-store/store";
import {
  getQuotationRequestsUrl,
  updateQuotationRequestURL,
  getQuotationRequestUrl
} from "../../../../utils/urls";
import { getItem } from "../../../../utils/localStorage";

const QuotationsManagement = () => {
  const navigate = useNavigate();
  const selectedFacilityId = useFacilityStore((state) => state.facilityId);

  // State
  const [loading, setLoading] = useState(false);
  const [quotations, setQuotations] = useState([]);
  const [filteredQuotations, setFilteredQuotations] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [currentQuotation, setCurrentQuotation] = useState(null);
  const [availableSuppliers, setAvailableSuppliers] = useState([]);
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [statusFilter, setStatusFilter] = useState('ALL');

  // New state for user and approval
  const [currentUser, setCurrentUser] = useState(null);
  const [commentsDialogVisible, setCommentsDialogVisible] = useState(false);
  const [comments, setComments] = useState('');
  const [selectedQuotationId, setSelectedQuotationId] = useState(null);
  const [approvalAction, setApprovalAction] = useState(null);

  // Status options for filtering
  const statusOptions = [
    { label: 'ALL', value: 'ALL' },
    { label: 'PENDING', value: 'PENDING' },
    { label: 'ACTIVE', value: 'ACTIVE' },
    { label: 'CLOSED', value: 'CLOSED' }
  ];

  const rfqTypes = ["Service", "Product", "Maintenance", "Construction"];
  const currencies = ["KES", "USD", "EUR", "GBP"];
  const evaluationTypes = [
    { label: "Automatic", value: "automatic" },
    { label: "Approvers", value: "approvers" }
  ];

  // Get current user when component mounts
  useEffect(() => {
    const getCurrentUser = async () => {
      try {
        const currentUserValue = await getItem("APPUSER");
        
        let parsedUser;
        
        // Check if the value needs to be parsed
        if (typeof currentUserValue === "string") {
          try {
            parsedUser = JSON.parse(currentUserValue);
          } catch (err) {
            console.error("Error parsing APPUSER:", err);
          }
        } else {
          parsedUser = currentUserValue; // Assume it's already an object
        }
        
        // Safely access and set user details
        if (parsedUser?.user) {
          setCurrentUser(parsedUser.user);
        } else {
          console.error("Invalid user structure:", parsedUser);
        }
      } catch (err) {
        console.log("Error getting user:", err.message);
      }
    };
    
    getCurrentUser();
  }, []);

  // Check if current user is an approver for a specific request
  const isUserApprover = (quotation) => {
    if (!currentUser?.userId || !Array.isArray(quotation.approvals)) return false;
  
    // find the approvals object for the current step
    const step = quotation.approvals.find(s => s.stepNumber === quotation.currentStep);
    if (!step || !Array.isArray(step.approvers)) return false;
  
    return step.approvers.some(approver =>
      String(approver.userId) === currentUser.userId &&
      approver.status === 'pending'
    );
  };

  // Check if quotation has been approved by the current user
  const hasUserApproved = (quotation) => {
    if (!currentUser?.userId || !quotation.approvals) return false;
    
    // Check if user has already approved in any step
    for (const step of quotation.approvals) {
      if (!Array.isArray(step.approvers)) continue;
      
      const hasApproved = step.approvers.some(approver => 
        String(approver.userId) === currentUser.userId && 
        approver.status === 'approved'
      );
      
      if (hasApproved) return true;
    }
    
    return false;
  };

  // Fetch quotations
  const fetchQuotations = useCallback(async () => {
    if (!selectedFacilityId) {
      toastify("No facility selected", "error");
      return;
    }

    try {
      setLoading(true);
      const response = await makeRequest2(
        `${getQuotationRequestsUrl}/${selectedFacilityId}`,
        "GET"
      );

      if (response.success && Array.isArray(response.data?.data)) {
        setQuotations(response.data.data);
        setFilteredQuotations(response.data.data);
      } else {
        setQuotations([]);
        setFilteredQuotations([]);
        throw new Error("Failed to fetch quotations");
      }
    } catch (error) {
      toastify(error.message || "Error fetching quotations", "error");
    } finally {
      setLoading(false);
    }
  }, [selectedFacilityId]);

  // Open comments dialog for approval/rejection
  const openCommentsDialog = (id, action) => {
    setSelectedQuotationId(id);
    setApprovalAction(action);
    setComments('');
    setCommentsDialogVisible(true);
  };

  // Submit approval/rejection with comments
  const submitApprovalAction = async () => {
    if (!selectedQuotationId || !approvalAction) return;
    
    try {
      setLoading(true);
      setCommentsDialogVisible(false);
      
      // Determine endpoint and parameters based on action
      const endpoint = `/api/app/quotations/approve/${selectedQuotationId}`;
      const approve = approvalAction === 'approve';
      
      const response = await makeRequest2(
        endpoint,
        'PUT',
        { 
          approve,
          comments,
          userId: currentUser.userId 
        }
      );
      
      if (response.success) {
        toastify(
          response.message || 
          `Quotation request ${approve ? 'approved' : 'rejected'} successfully`, 
          'success'
        );
        fetchQuotations();
      } else {
        toastify(
          response.error || 
          `Failed to ${approve ? 'approve' : 'reject'} quotation request`, 
          'error'
        );
      }
    } catch (error) {
      console.error("Action error:", error);
      toastify(`Error ${approvalAction === 'approve' ? 'approving' : 'rejecting'} quotation request`, 'error');
    } finally {
      setLoading(false);
      setSelectedQuotationId(null);
      setApprovalAction(null);
    }
  };

  // Filter quotations
  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
  };

  const filteredQuotationsList = filteredQuotations.filter((item) => {
    const searchMatch = 
      (item.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.rfqType || '').toLowerCase().includes(searchTerm.toLowerCase());

    const statusMatch = 
      statusFilter === 'ALL' || 
      (item.status || '').toUpperCase() === statusFilter;

    return searchMatch && statusMatch;
  });

  // Action template for quotation actions
  const actionTemplate = (rowData) => {
    const isApprover = isUserApprover(rowData);
    const hasApproved = hasUserApproved(rowData);
    const isPending = 
      (rowData.approvalStatus || '').toLowerCase() === 'pending' || 
      (rowData.approvalStatus || '').toLowerCase() === 'in_progress';
    
    // If user has already approved, show only view button
    if (hasApproved) {
      return (
        <div className="d-flex gap-2">
          <Button
            icon="ti ti-eye"
            className="p-button-rounded p-button-info"
            onClick={() => handleViewQuotation(rowData._id)}
            tooltip="View"
          />
        </div>
      );
    }
    
    // If user is an approver and request is in pending approval state
    if (isApprover && isPending) {
      return (
        <div className="d-flex gap-2">
          <Button
            icon="ti ti-eye"
            className="p-button-rounded p-button-info"
            onClick={() => handleViewQuotation(rowData._id)}
            tooltip="View"
          />
          <Button
            icon="ti ti-check"
            className="p-button-rounded p-button-success"
            onClick={() => openCommentsDialog(rowData._id, 'approve')}
            tooltip="Approve"
          />
          <Button
            icon="ti ti-x"
            className="p-button-rounded p-button-danger"
            onClick={() => openCommentsDialog(rowData._id, 'reject')}
            tooltip="Reject"
          />
        </div>
      );
    }
    
    // For regular users or when request is not pending approval
    return (
      <div className="d-flex gap-2">
        <Button
          icon="ti ti-eye"
          className="p-button-rounded p-button-info"
          onClick={() => handleViewQuotation(rowData._id)}
          tooltip="View"
        />
        <Button
          icon="ti ti-edit"
          className="p-button-rounded p-button-warning"
          onClick={() => handleEditQuotation(rowData._id)}
          tooltip="Edit"
          disabled={(rowData.status || '').toLowerCase() !== 'pending'}
        />
        <Button
          icon="ti ti-archive"
          className="p-button-rounded p-button-danger"
          onClick={() => handleCloseQuotation(rowData._id)}
          tooltip="Close"
          disabled={(rowData.status || '').toLowerCase() === 'closed'}
        />
      </div>
    );
  };

  // Dialog footer for comments
  const commentsDialogFooter = (
    <div>
      <Button 
        label="Cancel" 
        icon="pi pi-times" 
        className="p-button-text" 
        onClick={() => setCommentsDialogVisible(false)} 
      />
      <Button 
        label={approvalAction === 'approve' ? 'Approve' : 'Reject'} 
        icon={approvalAction === 'approve' ? 'pi pi-check' : 'pi pi-times'} 
        className={approvalAction === 'approve' ? 'p-button-success' : 'p-button-danger'} 
        onClick={submitApprovalAction} 
      />
    </div>
  );

  const fetchSuppliers = useCallback(async () => {
    if (!selectedFacilityId) return;

    try {
      const response = await makeRequest2(
        `/api/app/suppliers/${selectedFacilityId}`,
        "GET"
      );

      if (response.success && Array.isArray(response.data)) {
        setAvailableSuppliers(
          response.data.map(supplier => ({
            label: supplier.name,
            value: supplier._id
          }))
        );
      }
    } catch (error) {
      console.error("Error fetching suppliers:", error);
    }
  }, [selectedFacilityId]);

  const fetchQuotationDetails = useCallback(async (quotationId) => {
    if (!selectedFacilityId || !quotationId) return null;

    try {
      setLoading(true);
      const response = await makeRequest2(
        `${getQuotationRequestUrl}/${selectedFacilityId}/${quotationId}`,
        "GET"
      );

      if (response.success && response.data) {
        // Return the actual data object from the response
        return response.data.data;
      } else {
        throw new Error("Failed to fetch quotation details");
      }
    } catch (error) {
      toastify(error.message || "Error fetching quotation details", "error");
      return null;
    } finally {
      setLoading(false);
    }
  }, [selectedFacilityId]);

  useEffect(() => {
    if (selectedFacilityId) {
      fetchQuotations();
      fetchSuppliers();
    }
  }, [selectedFacilityId, fetchQuotations, fetchSuppliers]);

  const handleSearch = (value) => {
    setSearchTerm(value);
    filterQuotations(value, selectedType);
  };

  const handleTypeChange = (type) => {
    setSelectedType(type);
    filterQuotations(searchTerm, type);
  };

  const filterQuotations = (search, type) => {
    let filtered = [...quotations];

    if (search) {
      filtered = filtered.filter(quotation =>
        quotation.name?.toLowerCase().includes(search.toLowerCase()) ||
        quotation.rfqType?.toLowerCase().includes(search.toLowerCase())
      );
    }

    if (type) {
      filtered = filtered.filter(quotation => quotation.rfqType === type);
    }

    setFilteredQuotations(filtered);
  };

  const handleViewQuotation = async (id) => {
    const quotationDetails = await fetchQuotationDetails(id);
    if (quotationDetails) {
      setCurrentQuotation(quotationDetails);
      setViewModalVisible(true);
    }
  };

  const handleEditQuotation = async (id) => {
    const quotationDetails = await fetchQuotationDetails(id);
    if (quotationDetails) {
      setCurrentQuotation(quotationDetails);

      // Set selected suppliers based on the suppliers array
      if (quotationDetails.suppliers && Array.isArray(quotationDetails.suppliers)) {
        setSelectedSuppliers(quotationDetails.suppliers);
      } else {
        setSelectedSuppliers([]);
      }

      setEditModalVisible(true);
    }
  };

  const handleCloseQuotation = (id) => {
    confirmDialog({
      message: 'Are you sure you want to close this RFQ?',
      header: 'Close Confirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          setLoading(true);
          const response = await makeRequest2(
            `${updateQuotationRequestURL}/${selectedFacilityId}/${id}`,
            "PUT",
            { status: "Closed" }
          );

          if (response.success) {
            toastify(response.message || "RFQ closed successfully", "success");
            fetchQuotations();
          } else {
            toastify(response.error || "Failed to close RFQ", "error");
          }
        } catch (error) {
          toastify("Error closing RFQ", "error");
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handleUpdateQuotation = async () => {
    if (!currentQuotation || !currentQuotation._id) {
      toastify("Invalid quotation data", "error");
      return;
    }

    try {
      setLoading(true);

      // Prepare the update payload
      const updateData = {
        ...currentQuotation,
        suppliers: selectedSuppliers // Update with the selected supplier IDs
      };

      const response = await makeRequest2(
        `${updateQuotationRequestURL}/${selectedFacilityId}/${currentQuotation._id}`,
        "PUT",
        updateData
      );

      if (response.success) {
        toastify(response.message || "RFQ updated successfully", "success");
        setEditModalVisible(false);
        fetchQuotations();
      } else {
        toastify(response.error || "Failed to update RFQ", "error");
      }
    } catch (error) {
      toastify(error.message || "Error updating RFQ", "error");
    } finally {
      setLoading(false);
    }
  };

  const statusTemplate = (rowData) => {
    const statusStyles = {
      Active: { color: '#28a745', backgroundColor: '#e9fbe9' },
      Closed: { color: '#dc3545', backgroundColor: '#fde9e9' },
      Pending: { color: '#ffc107', backgroundColor: '#fff8e1' }
    };

    const status = rowData.status || "Active";
    const style = statusStyles[status] || {
      color: '#6c757d',
      backgroundColor: '#f1f1f1',
    };

    return (
      <span
        style={{
          ...style,
          padding: '0.5em 1em',
          borderRadius: '12px',
          fontWeight: 'bold',
          fontSize: '0.9rem',
          display: 'inline-block',
        }}
      >
        {status}
      </span>
    );
  };

  const dateTemplate = (rowData) => {
    return new Date(rowData.createdAt).toLocaleDateString();
  };

  const periodTemplate = (rowData) => {
    if (!rowData.startDate || !rowData.closingDate) {
      return "Not specified";
    }

    const formatDate = (dateString) => {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = date.toLocaleString('default', { month: 'short' }).toUpperCase();
      const day = date.getDate();
      return `${year}-${month}-${day}`;
    };

    const startDateFormatted = formatDate(rowData.startDate);
    const closingDateFormatted = formatDate(rowData.closingDate);

    return `${startDateFormatted} - ${closingDateFormatted}`;
  };

  const feesTemplate = (rowData) => {
    const currency = rowData.currency || '';
    const amount = (rowData.rfqFee || 0).toLocaleString();
    return `${currency} ${amount}`;
  };

  const actionBodyTemplate = (rowData) => {
    return (
      <div className="d-flex gap-2">
        <Button
          icon="ti ti-eye"
          className="p-button-rounded p-button-info"
          onClick={() => handleViewQuotation(rowData._id)}
          tooltip="View"
        />
        <Button
          icon="ti ti-edit"
          className="p-button-rounded p-button-warning"
          onClick={() => handleEditQuotation(rowData._id)}
          tooltip="Edit"
          disabled={rowData.status === 'Closed'}
        />
        <Button
          icon="ti ti-archive"
          className="p-button-rounded p-button-danger"
          onClick={() => handleCloseQuotation(rowData._id)}
          tooltip="Close"
          disabled={rowData.status === 'Closed'}
        />
      </div>
    );
  };

  // Edit Modal Footer
  const editModalFooter = (
    <div>
      <Button
        label="Cancel"
        icon="pi pi-times"
        className="p-button-text"
        onClick={() => setEditModalVisible(false)}
      />
      <Button
        label="Update"
        icon="pi pi-check"
        className="p-button-primary"
        onClick={handleUpdateQuotation}
        loading={loading}
      />
    </div>
  );

  // View Modal Footer
  const viewModalFooter = (
    <Button
      label="Close"
      icon="pi pi-times"
      className="p-button-text"
      onClick={() => setViewModalVisible(false)}
    />
  );

  // Helper to format dates for ISO string for inputs
  const formatDateForInput = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString);
  };

  if (!selectedFacilityId) {
    return (
      <Layout>
        <div className="card">
          <div className="card-body text-center">
            Please select a facility to view quotations.
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Page Header with Breadcrumbs */}
      <div className="page-header">
        <div className="page-block">
          <div className="row align-items-center">
            <div className="col-md-12">
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/facility/">Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="">Requests For Quotation</Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Main Card */}
      <div className="row">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-header">
              <h5>Requests For Quotation</h5>
            </div>
            <div className="card-body">
              <ConfirmDialog />

              {/* Filters and Add Button */}
              <div className="row mb-3">
                <div className="col-md-4">
                  <input
                    className="form-control"
                    placeholder="Search by name or type"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <div className="col-md-8 text-end">
                  <button
                    className="btn btn-primary"
                    onClick={() =>
                      navigate("/facility/procurement_management/quotations/add")
                    }
                  >
                    Add New RFQ
                  </button>
                </div>
              </div>

              {/* Status Filter Buttons */}
              <div className="row mb-3">
                <div className="col-12">
                  <div className="d-flex flex-wrap gap-2">
                    {statusOptions.map(option => (
                      <button
                        key={option.value}
                        className={`btn ${statusFilter === option.value ? 'btn-primary' : 'btn-outline-secondary'} text-uppercase`}
                        onClick={() => handleStatusFilterChange(option.value)}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* DataTable */}
              <DataTable
                value={filteredQuotationsList}
                paginator
                rows={10}
                loading={loading}
                emptyMessage="No quotations found"
                sortMode="multiple"
                removableSort
                stripedRows
                tableStyle={{ minWidth: '50rem' }}
              >
                <Column
                  field="createdAt"
                  header="Created"
                  sortable
                  body={dateTemplate}
                />
                <Column
                  field="name"
                  header="Name"
                  sortable
                  body={(rowData) => (
                    <Link to={`/facility/procurement_management/quotations/responses/${rowData._id}`}>
                      {rowData.name}
                    </Link>
                  )}
                />
                <Column field="rfqType" header="Category" sortable />
                <Column
                  field="period"
                  header="Period"
                  sortable
                  body={periodTemplate}
                />
                <Column
                  field="fees"
                  header="Fees"
                  sortable
                  body={feesTemplate}
                />
                <Column
                  field="status"
                  header="Status"
                  sortable
                  body={statusTemplate}
                />
                <Column
                  header="Actions"
                  body={actionTemplate}
                  style={{ width: '12rem' }}
                />
              </DataTable>
            </div>
          </div>
        </div>
      </div>

      {/* Comments Dialog for Approval/Rejection */}
      <Dialog
        header={`${approvalAction === 'approve' ? 'Approve' : 'Reject'} Quotation Request`}
        visible={commentsDialogVisible}
        style={{ width: '500px' }}
        footer={commentsDialogFooter}
        onHide={() => setCommentsDialogVisible(false)}
      >
        <div className="field">
          <label htmlFor="comments">Comments (Optional)</label>
          <InputTextarea
            id="comments"
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            rows={5}
            cols={30}
            className="w-100"
            placeholder="Enter any comments regarding your decision..."
          />
        </div>
      </Dialog>

      {/* Edit RFQ Modal */}
      <Dialog
        header="Edit Request For Quotation"
        visible={editModalVisible}
        style={{ width: '80vw' }}
        footer={editModalFooter}
        onHide={() => setEditModalVisible(false)}
      >
        {currentQuotation && (
          <div className="p-fluid">
            <div className="p-grid p-formgrid">
              <div className="row mb-3">
                <div className="col-md-6">
                  <label htmlFor="name" className="mb-1">Name</label>
                  <InputText
                    id="name"
                    value={currentQuotation.name || ""}
                    onChange={(e) => setCurrentQuotation({
                      ...currentQuotation,
                      name: e.target.value
                    })}
                  />
                </div>
                <div className="col-md-6">
                  <label htmlFor="rfqNumber" className="mb-1">RFQ Number</label>
                  <InputText
                    id="rfqNumber"
                    value={currentQuotation.rfqNumber || ""}
                    disabled
                  />
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-6">
                  <label htmlFor="startDate" className="mb-1">Start Date</label>
                  <Calendar
                    id="startDate"
                    value={formatDateForInput(currentQuotation.startDate)}
                    onChange={(e) => setCurrentQuotation({
                      ...currentQuotation,
                      startDate: e.value
                    })}
                    showIcon
                  />
                </div>
                <div className="col-md-6">
                  <label htmlFor="closingDate" className="mb-1">Closing Date</label>
                  <Calendar
                    id="closingDate"
                    value={formatDateForInput(currentQuotation.closingDate)}
                    onChange={(e) => setCurrentQuotation({
                      ...currentQuotation,
                      closingDate: e.value
                    })}
                    showIcon
                  />
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-4">
                  <label htmlFor="currency" className="mb-1">Currency</label>
                  <Dropdown
                    id="currency"
                    value={currentQuotation.currency || "KES"}
                    options={currencies}
                    onChange={(e) => setCurrentQuotation({
                      ...currentQuotation,
                      currency: e.value
                    })}
                  />
                </div>
                <div className="col-md-4">
                  <label htmlFor="rfqType" className="mb-1">Category</label>
                  <Dropdown
                    id="rfqType"
                    value={currentQuotation.category || ""}
                    options={rfqTypes}
                    onChange={(e) => setCurrentQuotation({
                      ...currentQuotation,
                      category: e.value
                    })}
                  />
                </div>
                <div className="col-md-4">
                  <label htmlFor="rfqFee" className="mb-1">RFQ Fee</label>
                  <InputNumber
                    id="rfqFee"
                    value={currentQuotation.rfqFee || 0}
                    onValueChange={(e) => setCurrentQuotation({
                      ...currentQuotation,
                      rfqFee: e.value
                    })}
                    mode="currency"
                    currency={currentQuotation.currency || "KES"}
                    locale="en-US"
                  />
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-6">
                  <label htmlFor="rfqEvaluationType" className="mb-1">Evaluation Type</label>
                  <Dropdown
                    id="rfqEvaluationType"
                    value={currentQuotation.rfqEvaluationType || "automatic"}
                    options={evaluationTypes}
                    optionLabel="label"
                    optionValue="value"
                    onChange={(e) => setCurrentQuotation({
                      ...currentQuotation,
                      rfqEvaluationType: e.value
                    })}
                  />
                </div>
                <div className="col-md-6">
                  <label htmlFor="status" className="mb-1">Status</label>
                  <Dropdown
                    id="status"
                    value={currentQuotation.status || "Pending"}
                    options={["Pending", "Active", "Closed"]}
                    onChange={(e) => setCurrentQuotation({
                      ...currentQuotation,
                      status: e.value
                    })}
                  />
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-12">
                  <label htmlFor="suppliers" className="mb-1">Suppliers</label>
                  <MultiSelect
                    id="suppliers"
                    value={selectedSuppliers}
                    options={availableSuppliers}
                    onChange={(e) => setSelectedSuppliers(e.value)}
                    display="chip"
                    placeholder="Select Suppliers"
                  />
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-12">
                  <label htmlFor="notes" className="mb-1">Notes</label>
                  <InputText
                    id="notes"
                    value={currentQuotation.notes || ""}
                    onChange={(e) => setCurrentQuotation({
                      ...currentQuotation,
                      notes: e.target.value
                    })}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </Dialog>

      {/* View RFQ Modal */}
      <Dialog
        header="Request For Quotation Details"
        visible={viewModalVisible}
        style={{ width: '80vw' }}
        footer={viewModalFooter}
        onHide={() => setViewModalVisible(false)}
      >
        {currentQuotation && (
          <div className="p-fluid">
            <div className="row mb-3">
              <div className="col-md-6">
                <h6>Basic Information</h6>
                <div className="d-flex flex-column gap-2">
                  <div>
                    <strong>Name:</strong> {currentQuotation.name}
                  </div>
                  <div>
                    <strong>RFQ Number:</strong> {currentQuotation.rfqNumber}
                  </div>
                  <div>
                    <strong>Category:</strong> {currentQuotation.category}
                  </div>
                  <div>
                    <strong>Status:</strong> {currentQuotation.status}
                  </div>
                  <div>
                    <strong>Created:</strong> {new Date(currentQuotation.createdAt).toLocaleDateString()}
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <h6>Timeline & Costs</h6>
                <div className="d-flex flex-column gap-2">
                  <div>
                    <strong>Start Date:</strong> {new Date(currentQuotation.startDate).toLocaleDateString()}
                  </div>
                  <div>
                    <strong>Closing Date:</strong> {new Date(currentQuotation.closingDate).toLocaleDateString()}
                  </div>
                  <div>
                    <strong>Evaluation Type:</strong> {currentQuotation.rfqEvaluationType}
                  </div>
                  <div>
                    <strong>Currency:</strong> {currentQuotation.currency}
                  </div>
                  <div>
                    <strong>RFQ Fee:</strong> {currentQuotation.currency} {(currentQuotation.rfqFee || 0).toLocaleString()}
                  </div>
                </div>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-12">
                <h6>Notes</h6>
                <p>{currentQuotation.notes || "No notes available."}</p>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-md-12">
                <h6>Items</h6>
                {currentQuotation.items && currentQuotation.items.length > 0 ? (
                  <DataTable value={currentQuotation.items} stripedRows>
                    <Column field="itemName" header="Item Name" />
                    <Column field="description" header="Description" />
                    <Column field="quantity" header="Quantity" />
                    <Column field="unitOfMeasure" header="Unit" />
                    <Column field="specifications" header="Specifications" />
                  </DataTable>
                ) : (
                  <p>No items available.</p>
                )}
              </div>
            </div>

            {/* <div className="row mb-3">
              <div className="col-md-12">
                <h6>Suppliers</h6>
                {currentQuotation.suppliersInfo && currentQuotation.suppliersInfo.length > 0 ? (
                  <DataTable value={currentQuotation.suppliersInfo} stripedRows>
                    <Column field="name" header="Supplier Name" />
                    <Column field="email" header="Email" />
                    <Column field="phone" header="Phone Number" />
                  </DataTable>
                ) : (
                  <p>No suppliers assigned to this RFQ.</p>
                )}
              </div>
            </div> */}
          </div>
        )}
      </Dialog>
    </Layout>
  );
};

export default QuotationsManagement;